import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { FiInfo } from 'react-icons/fi';
import { SAMPLE_SECTORS } from '../constants/sampleSectors';
import { apiUrl } from '../utils/api';
import './IngresoMuestrasTable.css';

const TYPES = [['ANIMAL', 'Animal'], ['VEGETAL', 'Vegetal'], ['AGUA', 'Agua'], ['OTRO', 'Otro']];
const GROUPS = [
  ['VACUNO', 'Vacuno'], ['OVINO', 'Ovino'], ['CAPRINO', 'Caprino'], ['PORCINO', 'Porcino'],
  ['EQUINO', 'Equino'], ['CAMELIDOS_SUDAMERICANOS', 'Camélidos sudamericanos'],
  ['CANINO', 'Canino'], ['FELINO', 'Felino'], ['AVES_DE_CORRAL', 'Aves de corral'],
  ['ANIMALES_EXOTICOS', 'Animales exóticos'], ['ANIMALES_SILVESTRES', 'Animales silvestres'], ['VARIOS', 'Varios'],
];
const VEGETABLES = [['HOJA', 'Hoja'], ['RAIZ', 'Raíz'], ['FRUTO', 'Fruto'], ['SEMILLA', 'Semilla'], ['TALLO', 'Tallo'], ['OTRO', 'Otro']];
const blankSample = (sectorInicial = '') => ({
  identificacion: '', sector: '', tipo_muestra: '', grupo_zootecnico: '', numero_de_muestras: 1,
  fecha_recoleccion: '', aisa_tomo_muestra: false, nombre_operario_INTA: '',
  cantidad_grupos_zootecnicos: '', tipo_muestra_vegetal: '', descripcion_vegetal: '',
  otro_tipo_muestra: '', observaciones: '', numero_manual: '',
  ...(sectorInicial === 'PACK_TORO' ? { sector: 'PACK_TORO', tipo_muestra: 'ANIMAL', grupo_zootecnico: 'VACUNO', grupo_vacuno: 'TORO', sexo: 'MACHO' } : {}),
});
const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Token ${token}` } : {};
};
const errorText = (data) => {
  if (typeof data === 'string') return data;
  if (!data || typeof data !== 'object') return '';
  return Object.values(data).map(errorText).filter(Boolean).join(' ');
};
const manualProtocol = (sample) => `${sample.sector === 'PACK_TORO' ? 'TP' : sample.sector}-${sample.numero_manual}`;
const existingRow = (sample) => ({
  id: `existing-${sample.protocolo_muestra}`,
  sample: Object.fromEntries(Object.entries(blankSample()).map(([field, fallback]) => [field, sample[field] ?? fallback])),
  saved: sample,
  error: '',
});

export default function IngresoMuestrasTable({ protocoloGeneral = '', sectorInicial = '', onSaved }) {
  const nextId = useRef(2);
  const savingRef = useRef(false);
  const noticeRef = useRef(null);
  const topScrollRef = useRef(null);
  const topScrollContentRef = useRef(null);
  const tableScrollRef = useRef(null);
  const tableRef = useRef(null);
  const [protocol, setProtocol] = useState(protocoloGeneral);
  const [protocols, setProtocols] = useState([]);
  const [users, setUsers] = useState([]);
  const [rows, setRows] = useState(() => [{ id: 1, sample: blankSample(sectorInicial), saved: null, error: '' }]);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const [existing, setExisting] = useState({ protocol: '', rows: [], status: 'idle' });
  const [reload, setReload] = useState(0);
  const [protocolPreviews, setProtocolPreviews] = useState({});
  const [previewReload, setPreviewReload] = useState(0);

  useEffect(() => {
    const updateScrollWidth = () => {
      topScrollContentRef.current.style.width = `${tableScrollRef.current.scrollWidth}px`;
      topScrollRef.current.scrollLeft = tableScrollRef.current.scrollLeft;
    };
    updateScrollWidth();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateScrollWidth) : null;
    observer?.observe(tableRef.current);
    observer?.observe(tableScrollRef.current);
    window.addEventListener('resize', updateScrollWidth);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', updateScrollWidth);
    };
  }, []);

  const syncScroll = (event, targetRef) => {
    if (targetRef.current.scrollLeft !== event.currentTarget.scrollLeft) {
      targetRef.current.scrollLeft = event.currentTarget.scrollLeft;
    }
  };

  useEffect(() => {
    if (notice?.summary) noticeRef.current?.focus();
  }, [notice]);
  const selectedProtocol = protocol.trim();
  const pending = rows.filter((row) => !row.saved);
  const savedProtocols = new Set(rows.filter((row) => row.saved).map((row) => row.saved.protocolo_muestra));
  const existingRows = existing.protocol === selectedProtocol
    ? existing.rows.filter((row) => !savedProtocols.has(row.saved.protocolo_muestra)) : [];
  const visibleRows = [...existingRows, ...rows];
  const loadingExisting = Boolean(selectedProtocol) && (existing.protocol !== selectedProtocol || existing.status === 'loading');
  const previewSectors = [...new Set(pending.map((row) => row.sample.sector).filter(Boolean))].sort().join('|');
  const savedSignature = [...savedProtocols].sort().join('|');
  const previewNumbers = {};
  Object.entries(protocolPreviews).forEach(([sector, preview]) => {
    if (preview.protocol) previewNumbers[sector] = Number(preview.protocol.split('-').pop());
  });
  // Account for earlier rows in this batch, including manual numbers and confirmed saves.
  rows.filter((row) => row.saved).forEach((row) => {
    const sector = row.sample.sector;
    if (previewNumbers[sector]) previewNumbers[sector] = Math.max(previewNumbers[sector], Number(row.saved.protocolo_muestra.split('-').pop()) + 1);
  });
  const rowPreviews = {};
  pending.forEach((row) => {
    const { sector, numero_manual } = row.sample;
    if (row.manualEnabled) {
      if (sector && /^\d{3,5}$/.test(numero_manual)) {
        rowPreviews[row.id] = manualProtocol(row.sample);
        if (previewNumbers[sector]) previewNumbers[sector] = Math.max(previewNumbers[sector], Number(numero_manual) + 1);
      }
    } else if (previewNumbers[sector]) {
      rowPreviews[row.id] = `${sector === 'PACK_TORO' ? 'TP' : sector}-${String(previewNumbers[sector]++).padStart(3, '0')}`;
    }
  });

  useEffect(() => {
    let active = true;
    setProtocolPreviews({});
    previewSectors.split('|').filter(Boolean).forEach((sector) => {
      axios.get(apiUrl(`/api/next-protocolo/?sector=${encodeURIComponent(sector)}`), { headers: authHeaders() })
        .then(({ data }) => {
          if (!/^[A-Z]+-\d+$/.test(data?.protocolo || '')) throw new Error('Protocolo inválido');
          if (active) setProtocolPreviews((current) => ({ ...current, [sector]: { protocol: data.protocolo } }));
        })
        .catch(() => {
          if (active) setProtocolPreviews((current) => ({ ...current, [sector]: { error: true } }));
        });
    });
    return () => { active = false; };
  }, [previewSectors, savedSignature, previewReload]);

  useEffect(() => {
    if (!selectedProtocol) return;
    let active = true;
    setExisting({ protocol: selectedProtocol, rows: [], status: 'loading' });
    const timer = setTimeout(() => {
      axios.get(apiUrl(`/api/ingresos_muestras/?protocolo_general=${encodeURIComponent(selectedProtocol)}`), { headers: authHeaders() })
        .then(({ data }) => {
          if (active) setExisting({ protocol: selectedProtocol, rows: (Array.isArray(data) ? data : []).map(existingRow), status: 'loaded' });
        })
        .catch(() => {
          if (active) setExisting({ protocol: selectedProtocol, rows: [], status: 'error' });
        });
    }, 250);
    return () => { active = false; clearTimeout(timer); };
  }, [selectedProtocol, reload]);

  useEffect(() => {
    let active = true;
    axios.get(apiUrl('/api/usuarios/opciones/?solo_operarios=1'), { headers: authHeaders() })
      .then(({ data }) => { if (active) setUsers(Array.isArray(data) ? data : []); })
      .catch(() => { if (active) setNotice({ error: true, text: 'No se pudieron cargar los operarios. Vuelve a abrir la carga si necesitas seleccionar uno.' }); });
    if (!protocoloGeneral) {
      axios.get(apiUrl('/api/ingresos_casos/?compact=1'), { headers: authHeaders() })
        .then(({ data }) => { if (active) setProtocols(Array.isArray(data) ? data : []); })
        .catch(() => { if (active) setNotice({ error: true, text: 'No se pudieron cargar los casos. Puedes escribir el protocolo general.' }); });
    }
    return () => { active = false; };
  }, [protocoloGeneral]);

  const update = (id, field, value) => {
    setRows((current) => current.map((row) => {
      if (row.id !== id || row.saved) return row;
      const sample = { ...row.sample, [field]: value };
      if (field === 'sector' && value === 'PACK_TORO') {
        Object.assign(sample, { tipo_muestra: 'ANIMAL', grupo_zootecnico: 'VACUNO', grupo_vacuno: 'TORO', sexo: 'MACHO', identificacion: '' });
      }
      if (field === 'tipo_muestra') {
        sample.grupo_zootecnico = '';
        sample.cantidad_grupos_zootecnicos = '';
        sample.tipo_muestra_vegetal = '';
        sample.descripcion_vegetal = '';
        sample.otro_tipo_muestra = '';
      }
      if (field === 'grupo_zootecnico' && value !== 'VARIOS') sample.cantidad_grupos_zootecnicos = '';
      if (field === 'tipo_muestra_vegetal' && value !== 'OTRO') sample.descripcion_vegetal = '';
      if (field === 'aisa_tomo_muestra' && !value) sample.nombre_operario_INTA = '';
      return { ...row, sample, error: '' };
    }));
  };

  const addRow = (source) => {
    const id = nextId.current++;
    const sample = source ? { ...source, identificacion: '', numero_manual: '' } : blankSample(sectorInicial);
    setRows((current) => [...current, { id, sample, saved: null, error: '' }]);
    setNotice(null);
  };

  const toggleManualProtocol = (id, enabled) => {
    setRows((current) => current.map((row) => row.id === id && !row.saved ? {
      ...row,
      manualEnabled: enabled,
      sample: { ...row.sample, numero_manual: enabled ? (rowPreviews[id]?.split('-').pop() || '') : '' },
      error: '',
    } : row));
  };

  const save = async (event) => {
    event.preventDefault();
    if (savingRef.current || !pending.length) return;
    if (!protocol.trim()) {
      setNotice({ error: true, text: 'Completa el protocolo general del caso.' });
      return;
    }
    const seen = new Set();
    const validated = rows.map((row) => {
      if (row.saved) return row;
      const sample = row.sample;
      let error = '';
      if (!sample.sector || !sample.tipo_muestra) error = 'Selecciona el sector y el tipo de muestra.';
      else if (!Number.isInteger(Number(sample.numero_de_muestras)) || Number(sample.numero_de_muestras) < 1) error = 'La cantidad debe ser un entero mayor que cero.';
      else if (sample.tipo_muestra === 'ANIMAL' && sample.grupo_zootecnico === 'VARIOS' && (!Number.isInteger(Number(sample.cantidad_grupos_zootecnicos)) || Number(sample.cantidad_grupos_zootecnicos) < 2)) error = 'Indica al menos dos grupos zootécnicos.';
      else if (sample.tipo_muestra === 'VEGETAL' && (!sample.tipo_muestra_vegetal || (sample.tipo_muestra_vegetal === 'OTRO' && !sample.descripcion_vegetal.trim()))) error = 'Completa el tipo y la descripción vegetal.';
      else if (sample.tipo_muestra === 'OTRO' && !sample.otro_tipo_muestra.trim()) error = 'Describe el otro tipo de muestra.';
      else if (row.manualEnabled && !/^\d{3,5}$/.test(sample.numero_manual)) error = 'El número de protocolo debe tener entre 3 y 5 dígitos.';
      if (sample.numero_manual) {
        const value = manualProtocol(sample);
        if (seen.has(value)) error = `El protocolo ${value} está repetido en la tabla.`;
        seen.add(value);
      }
      return { ...row, error };
    });
    setRows(validated);
    if (validated.some((row) => row.error)) {
      setNotice({ error: true, text: 'Revisa las filas indicadas antes de guardar.' });
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setNotice(null);
    const created = [];
    const summary = [];
    let failures = 0;
    // Sequential requests let the server assign each automatic protocol after the previous save.
    for (const row of pending) {
      const { numero_manual, ...sample } = row.sample;
      const payload = {
        ...sample, protocolo_general: protocol.trim(), identificacion: sample.identificacion.trim(),
        fecha_recoleccion: sample.fecha_recoleccion || null,
        numero_de_muestras: Number(sample.numero_de_muestras),
        cantidad_grupos_zootecnicos: sample.cantidad_grupos_zootecnicos ? Number(sample.cantidad_grupos_zootecnicos) : null,
        ...(numero_manual ? { protocolo_muestra: manualProtocol(row.sample) } : {}),
      };
      try {
        const { data } = await axios.post(apiUrl('/api/ingresos_muestras/'), payload, { headers: authHeaders() });
        created.push(data);
        summary.push({ protocol: data.protocolo_muestra, quantity: Number(data.numero_de_muestras ?? payload.numero_de_muestras) });
        setRows((current) => current.map((item) => item.id === row.id ? { ...item, saved: data, error: '' } : item));
      } catch (error) {
        failures += 1;
        const message = error.response?.status < 500
          ? errorText(error.response.data) || 'No se pudo guardar la muestra.'
          : 'No se pudo confirmar el guardado. Revisa el caso antes de reintentar esta fila.';
        setRows((current) => current.map((item) => item.id === row.id ? { ...item, error: message } : item));
      }
    }
    setNotice({ error: failures > 0, summary, text: failures
      ? `${created.length} muestra(s) guardada(s). ${failures} fila(s) pendientes; revisa sus mensajes. Las filas guardadas no se enviarán otra vez.`
      : `${created.length} muestra(s) guardada(s). Puedes seguir agregando filas en esta tabla.` });
    savingRef.current = false;
    setSaving(false);
    if (created.length) onSaved?.(created);
  };

  return (
    <div className="sample-entry">
      <div className="form-highlight mb-3">
        <div><span className="section-kicker">Carga múltiple</span><h3 className="form-title">Agregar muestras al caso</h3>
          <p className="page-subtitle mt-2 mb-0">Selecciona el protocolo general del caso para agregar muestras y ver las muestras previamente guardadas.</p></div>
        <span className="protocol-chip">{protocol || 'Sin protocolo'}</span>
      </div>
      {notice && <div ref={noticeRef} tabIndex={-1} className={`alert ${notice.error ? 'subtle-alert' : 'alert-success'}`} role={notice.error ? 'alert' : 'status'} aria-label={notice.summary ? 'Resumen del guardado de muestras' : undefined}>
        {notice.summary && <strong className="d-block mb-2">Resumen del guardado</strong>}
        <p className="mb-0">{notice.text}</p>
        {notice.summary?.length > 0 && <>
          <p className="mt-2 mb-2">Cantidad total de muestras: <strong>{notice.summary.reduce((total, item) => total + item.quantity, 0)}</strong> · Protocolos creados: <strong>{notice.summary.length}</strong></p>
          <div className="table-responsive">
            <table className="table table-sm mb-0" aria-label="Protocolos creados en este guardado">
              <thead><tr><th scope="col">Protocolo definitivo</th><th scope="col">Cantidad de muestras</th></tr></thead>
              <tbody>{notice.summary.map((item) => <tr key={item.protocol}><th scope="row">{item.protocol}</th><td>{item.quantity}</td></tr>)}</tbody>
            </table>
          </div>
        </>}
      </div>}
      <form onSubmit={save} noValidate>
        <fieldset disabled={saving}>
          <div className="mb-3">
            <label className="form-label" htmlFor="sample-entry-protocol">Protocolo general</label>
            <input id="sample-entry-protocol" className="form-control" value={protocol} required
              readOnly={Boolean(protocoloGeneral) || rows.some((row) => row.saved)}
              onChange={(event) => setProtocol(event.target.value)} list="sample-entry-cases" placeholder="Buscar o escribir el protocolo del caso" />
            <datalist id="sample-entry-cases">{protocols.map((item) => <option key={item.protocolo_general} value={item.protocolo_general} />)}</datalist>
          </div>
          {loadingExisting && (
            <div className="d-flex align-items-center gap-2 text-muted mb-3" role="status">
              <span className="spinner-border spinner-border-sm text-primary" aria-hidden="true" />
              <span>Cargando muestras del caso...</span>
            </div>
          )}
          {selectedProtocol && existing.protocol === selectedProtocol && existing.status === 'error' && (
            <div className="alert subtle-alert" role="alert">
              No se pudieron cargar las muestras existentes de este caso.
              <button type="button" className="btn btn-outline-brand btn-sm ms-2" onClick={() => setReload((current) => current + 1)}>Reintentar carga</button>
            </div>
          )}
          {selectedProtocol && existing.protocol === selectedProtocol && existing.status === 'loaded' && (
            <p className="text-muted small">{visibleRows.some((row) => row.saved)
              ? 'Las muestras guardadas del caso aparecen en la tabla. Puedes duplicarlas para cargar una nueva.'
              : 'Este caso todavía no tiene muestras guardadas.'}</p>
          )}
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <span className="text-muted">{pending.length} pendiente(s) · {visibleRows.length - pending.length} guardada(s)</span>
          </div>
          <p className="text-muted small">El protocolo previsto no está reservado: puede cambiar si otra persona guarda antes en el mismo sector. El definitivo se confirma al guardar.</p>
          <p id="sample-identification-help" className="text-muted small">La identificación es opcional.</p>
          {visibleRows.some((row) => row.sample.sector === 'PACK_TORO') && <div className="alert alert-info" role="note"><strong>En caso de cargar una muestra para el servicio de Pack Toro: aquí debes indicar el número total de animales.</strong> Puedes comenzar con 1: si después guardas más animales en las cargas individuales de Pack Toro, el total de esta muestra general aumentará automáticamente. No es necesario identificar cada animal. Después de guardar el lote, ingresa al sector Pack Toro para realizar las cargas individuales. Esta indicación no aplica a los demás sectores.</div>}
          <div ref={topScrollRef} className="sample-entry__top-scroll" tabIndex={0} role="region" aria-label="Desplazamiento horizontal superior de muestras" onScroll={(event) => syncScroll(event, tableScrollRef)}>
            <div ref={topScrollContentRef} className="sample-entry__top-scroll-content" />
          </div>
          <div ref={tableScrollRef} className="table-responsive sample-entry__scroll" tabIndex={0} role="region" aria-label="Tabla de carga de muestras" onScroll={(event) => syncScroll(event, topScrollRef)}>
            <table ref={tableRef} className="table table-bordered align-middle sample-entry__table">
              <thead><tr>{['Sector', 'Protocolo de muestra', 'Tipo de muestra', 'Grupo / detalle', 'Cantidad', 'Identificación del animal', 'Más datos', 'Acciones'].map((label) => <th scope="col" key={label}>{label}</th>)}</tr></thead>
              <tbody>{visibleRows.map((row, index) => {
                const sample = row.sample;
                const number = index + 1;
                const input = (field, label, extra = {}) => <input className="form-control" aria-label={`${label} ${number}`} value={sample[field]} disabled={Boolean(row.saved) || !sample.sector} onChange={(event) => update(row.id, field, event.target.value)} {...extra} />;
                const select = (field, label, options) => <select className="form-select" aria-label={`${label} ${number}`} value={sample[field]} disabled={Boolean(row.saved) || (field !== 'sector' && !sample.sector)} onChange={(event) => update(row.id, field, event.target.value)}><option value="">Seleccionar</option>{options.map(([value, text]) => <option key={value} value={value}>{text}</option>)}</select>;
                return <React.Fragment key={row.id}>
                  <tr className={row.saved ? 'table-success' : row.error ? 'table-warning' : ''}>
                    <th scope="row">
                      <span className="d-block small mb-1">Fila {number}</span>
                      {select('sector', 'Sector de muestra', SAMPLE_SECTORS.map(({ value, label }) => [value, label]))}
                      <details className="sample-entry__sector-help mt-2">
                        <summary aria-label={`Información sobre el sector de muestra ${number}`} title="Información sobre el sector"><FiInfo aria-hidden="true" /></summary>
                        <p className="small fw-normal mt-2 mb-0">Selecciona primero el sector para ver el protocolo previsto. Puedes duplicar filas para reutilizar sus datos.</p>
                      </details>
                    </th>
                    <td aria-label={`Protocolo de muestra ${number}`}>
                      {row.saved ? <><strong className="d-block">{row.saved.protocolo_muestra}</strong>{row.saved.protocolo_historico && <span className="badge bg-secondary me-2" title="Protocolo histórico conservado">old</span>}<span className="text-success small">Guardada</span></>
                        : !sample.sector ? <span className="text-muted small">Selecciona el sector</span>
                          : row.manualEnabled ? <>
                            <div className="input-group input-group-sm">
                              <span className="input-group-text">{sample.sector === 'PACK_TORO' ? 'TP' : sample.sector}-</span>
                              {input('numero_manual', 'Número de protocolo manual de muestra', { inputMode: 'numeric', maxLength: 5, placeholder: 'Ej. 001', 'aria-describedby': `manual-protocol-help-${row.id}` })}
                            </div>
                            <span id={`manual-protocol-help-${row.id}`} className="text-muted small">Manual · 3 a 5 dígitos</span>
                          </>
                          : rowPreviews[row.id] ? <><strong className="d-block">{rowPreviews[row.id]}</strong><span className="text-muted small">{sample.numero_manual ? 'Manual · pendiente' : 'Previsto · sin reservar'}</span></>
                            : protocolPreviews[sample.sector]?.error ? <><span className="text-muted small d-block">Vista previa no disponible</span><button type="button" className="btn btn-outline-brand btn-sm mt-1" onClick={() => setPreviewReload((current) => current + 1)}>Actualizar protocolo</button></>
                              : <span className="text-muted small">Consultando protocolo...</span>}
                      {!row.saved && <label className="form-check mt-2 mb-0 small">
                        <input type="checkbox" className="form-check-input" aria-label={`Usar protocolo manual de muestra ${number}`}
                          checked={Boolean(row.manualEnabled)} disabled={!sample.sector}
                          onChange={(event) => toggleManualProtocol(row.id, event.target.checked)} />
                        <span className="form-check-label">Ingresar manualmente</span>
                      </label>}
                    </td>
                    <td>{sample.sector === 'PACK_TORO' ? 'Animal' : select('tipo_muestra', 'Tipo de muestra', TYPES)}</td>
                    <td>
                      {sample.sector === 'PACK_TORO' ? 'Vacuno · Toro' : sample.tipo_muestra === 'ANIMAL' && <>{select('grupo_zootecnico', 'Grupo zootécnico de muestra', GROUPS)}{sample.grupo_zootecnico === 'VARIOS' && <div className="mt-2"><label className="form-label small">Cantidad de grupos</label>{input('cantidad_grupos_zootecnicos', 'Cantidad de grupos de muestra', { type: 'number', min: 2, step: 1 })}</div>}</>}
                      {sample.tipo_muestra === 'VEGETAL' && <>{select('tipo_muestra_vegetal', 'Tipo vegetal de muestra', VEGETABLES)}{sample.tipo_muestra_vegetal === 'OTRO' && <div className="mt-2">{input('descripcion_vegetal', 'Descripción vegetal de muestra', { maxLength: 200, placeholder: 'Descripción vegetal' })}</div>}</>}
                      {sample.tipo_muestra === 'OTRO' && input('otro_tipo_muestra', 'Descripción de muestra', { maxLength: 100, placeholder: 'Describe la muestra' })}
                      {(!sample.tipo_muestra || sample.tipo_muestra === 'AGUA') && <span className="text-muted">—</span>}
                    </td>
                    <td>{sample.sector === 'PACK_TORO' && <span className="small d-block mb-1">Total de animales</span>}{input('numero_de_muestras', 'Cantidad de muestras de fila', { type: 'number', min: 1, step: 1 })}</td>
                    <td>{sample.sector === 'PACK_TORO' ? <span className="small text-muted">Lote · sin identificación individual</span> : input('identificacion', 'Identificación de muestra', { maxLength: 100, placeholder: 'Ej. caravana 123 (opcional)', 'aria-describedby': 'sample-identification-help' })}</td>
                    <td><details><summary>Más datos <span className="visually-hidden">de muestra {number}</span></summary><div className="sample-entry__details">
                      <label className="form-label small">Fecha de recolección</label>{input('fecha_recoleccion', 'Fecha de recolección de muestra', { type: 'date' })}
                      <label className="form-check my-3"><input type="checkbox" className="form-check-input" aria-label={`AISA tomó muestra ${number}`} checked={sample.aisa_tomo_muestra} disabled={Boolean(row.saved)} onChange={(event) => update(row.id, 'aisa_tomo_muestra', event.target.checked)} /><span className="form-check-label">AISA tomó muestra</span></label>
                      {sample.aisa_tomo_muestra && <><label className="form-label small">Operario INTA</label>{select('nombre_operario_INTA', 'Operario de muestra', users.map((user) => [user.username, [user.first_name, user.last_name].filter(Boolean).join(' ') || user.username]))}</>}
                      <label className="form-label small mt-3">Observaciones</label><textarea className="form-control" rows={3} maxLength={2000} aria-label={`Observaciones de muestra ${number}`} disabled={Boolean(row.saved)} value={sample.observaciones} onChange={(event) => update(row.id, 'observaciones', event.target.value)} />
                    </div></details></td>
                    <td><div className="d-grid gap-2"><button type="button" className="btn btn-outline-brand btn-sm" aria-label={`Duplicar fila ${number}`} onClick={() => addRow(sample)}>Duplicar</button><button type="button" className="btn btn-outline-danger btn-sm" aria-label={`Quitar fila ${number}`} disabled={Boolean(row.saved) || visibleRows.length === 1} onClick={() => setRows((current) => current.filter((item) => item.id !== row.id))}>Quitar</button></div></td>
                  </tr>
                  {row.error && <tr><td colSpan={8}><div className="text-danger" role="alert">Fila {number}: {row.error}</div></td></tr>}
                </React.Fragment>;
              })}</tbody>
            </table>
          </div>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mt-3">
            <button type="button" className="btn btn-outline-brand" onClick={() => addRow()}>Agregar otra muestra</button>
            <button type="submit" className="btn btn-brand" disabled={!pending.length || saving}>{saving ? 'Guardando muestras...' : `Guardar muestras (${pending.length})`}</button>
          </div>
        </fieldset>
      </form>
      {rows.some((row) => row.saved && row.sample.sector === 'PACK_TORO') && <section className="mt-4 pt-3 border-top" aria-label="Cargas individuales de Pack Toro">
        <p className="mb-3">Los lotes Pack Toro guardados ya están disponibles para sus cargas individuales.</p>
        <div className="d-flex flex-column align-items-start gap-3">{rows.filter((row) => row.saved && row.sample.sector === 'PACK_TORO').map((row) => <div key={row.id}>
          <span className="small text-muted d-block mb-2">Lote {row.saved.protocolo_muestra} · {row.saved.numero_de_muestras ?? row.sample.numero_de_muestras} animales</span>
          <Link className={`btn btn-brand${saving ? ' disabled' : ''}`} aria-disabled={saving} tabIndex={saving ? -1 : undefined} to={`/pack-toro/${row.saved.protocolo_general || selectedProtocol}?tab=muestras&lote=${encodeURIComponent(row.saved.protocolo_muestra)}`}>Ingresar al sector Pack Toro para cargas individuales</Link>
        </div>)}</div>
      </section>}
    </div>
  );
}
